import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import styles from "../../styles/betaIAPopup.module.css";

const apiUrl = process.env.REACT_APP_API_URL || "https://api.systemicsurvey.com";

function BetaIAPopup({ isOpen, onClose, id_empresa, id_equipo, id_encuesta }) {
  const { t } = useTranslation();
  const [loadingIA, setLoadingIA] = useState(true);
  const [respuesta, setRespuesta] = useState(null);
  const [comentarios, setComentarios] = useState([]);
  const [loadingComentarios, setLoadingComentarios] = useState(true);
  const [nombreAutor, setNombreAutor] = useState("");
  const [comentario, setComentario] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);

  const fetchAnalisis = async () => {
    if (!id_empresa || !id_equipo || !id_encuesta) return;
    setLoadingIA(true);
    setError(null);
    try {
      const res = await fetch(`${apiUrl}/informes/betaIAAnalisis`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ id_empresa, id_equipo, id_encuesta }),
      });
      const data = await res.json();
      if (res.ok) {
        setRespuesta(data.respuesta ?? null);
      } else {
        setError(data.message || t("betaIA.errorLoading"));
      }
    } catch (e) {
      setError(e.message || t("betaIA.errorLoading"));
    } finally {
      setLoadingIA(false);
    }
  };

  const fetchComentarios = async () => {
    if (!id_encuesta) return;
    setLoadingComentarios(true);
    try {
      const res = await fetch(
        `${apiUrl}/informes/betaIAComentarios?id_encuesta=${encodeURIComponent(id_encuesta)}`,
        { credentials: "include" }
      );
      const data = await res.json();
      if (res.ok) setComentarios(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingComentarios(false);
    }
  };

  useEffect(() => {
    if (!isOpen || !id_encuesta) return;
    fetchAnalisis();
    fetchComentarios();
  }, [isOpen, id_empresa, id_equipo, id_encuesta]);

  const handleSubmitComentario = async (e) => {
    e.preventDefault();
    const nombre = nombreAutor.trim();
    const texto = comentario.trim();
    if (!nombre || !texto) return;
    setEnviando(true);
    try {
      const res = await fetch(`${apiUrl}/informes/betaIAComentarios`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          id_empresa,
          id_equipo,
          id_encuesta,
          nombre_autor: nombre,
          comentario: texto,
        }),
      });
      if (res.ok) {
        setNombreAutor("");
        setComentario("");
        setShowAddForm(false);
        fetchComentarios();
      }
    } catch (e) {
      console.error(e);
    } finally {
      setEnviando(false);
    }
  };

  const formatFecha = (fecha) => {
    if (!fecha) return "";
    const d = new Date(fecha);
    return d.toLocaleDateString(undefined, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className={styles.overlay}>
      <div className={styles.popup}>
        <div className={styles.header}>
          <h2>{t("betaIA.title")}</h2>
          <button type="button" className={styles.closeBtn} onClick={onClose} aria-label={t("close")}>
            ×
          </button>
        </div>
        <div className={styles.content}>
          <div className={styles.leftPanel}>
            <h3>{t("betaIA.analysisTitle")}</h3>
            {loadingIA ? (
              <div className={styles.loadingIA}>
                <div className={styles.loaderSpinner} />
                <p className={styles.loadingIAText}>{t("betaIA.generatingResponse")}</p>
                <p className={styles.loadingIASub}>{t("betaIA.generatingResponseSub")}</p>
              </div>
            ) : error ? (
              <div className={styles.error}>{error}</div>
            ) : (
              <>
                {respuesta && (
                  <div className={styles.block}>
                    <h4>{t("betaIA.responseTitle")}</h4>
                    <div className={styles.responseHtml} dangerouslySetInnerHTML={{ __html: respuesta }} />
                  </div>
                )}
                {!respuesta && !error && (
                  <p className={styles.noData}>{t("betaIA.noAnalysis")}</p>
                )}
              </>
            )}
          </div>
          <div className={styles.rightPanel}>
            <h3>{t("betaIA.commentsTitle")}</h3>
            {loadingComentarios ? (
              <div className={styles.loading}>{t("betaIA.loading")}</div>
            ) : (
              <ul className={styles.commentList}>
                {comentarios.length === 0 ? (
                  <li className={styles.noComments}>{t("betaIA.noComments")}</li>
                ) : (
                  comentarios.map((c) => (
                    <li key={c.id_comentario} className={styles.commentItem}>
                      <span className={styles.commentAuthor}>{c.nombre_autor}</span>
                      <span className={styles.commentDate}>{formatFecha(c.fecha_creacion)}</span>
                      <p className={styles.commentText}>{c.comentario}</p>
                    </li>
                  ))
)}
            </ul>
            )}
            {!showAddForm ? (
              <button
                type="button"
                className={styles.addCommentToggle}
                onClick={() => setShowAddForm(true)}
              >
                + {t("betaIA.addComment")}
              </button>
            ) : (
              <div className={styles.formWrap}>
                <form onSubmit={handleSubmitComentario} className={styles.form}>
                  <label>
                    {t("betaIA.yourName")}
                    <input
                      type="text"
                      value={nombreAutor}
                      onChange={(e) => setNombreAutor(e.target.value)}
                      placeholder={t("betaIA.yourNamePlaceholder")}
                      maxLength={255}
                      required
                    />
                  </label>
                  <label>
                    {t("betaIA.comment")}
                    <textarea
                      value={comentario}
                      onChange={(e) => setComentario(e.target.value)}
                      placeholder={t("betaIA.commentPlaceholder")}
                      rows={5}
                      required
                    />
                  </label>
                  <div className={styles.formActions}>
                    <button type="submit" className={styles.submitBtn} disabled={enviando}>
                      {enviando ? t("betaIA.sending") : t("betaIA.publish")}
                    </button>
                    <button
                      type="button"
                      className={styles.cancelBtn}
                      onClick={() => setShowAddForm(false)}
                    >
                      {t("betaIA.cancel")}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default BetaIAPopup;
